import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import {
  MenuDetails,
  Variant,
  StoreProduct,
  MenuCategoryType,
  VariantPriceUpdateRequest,
  ProductRemoveRequest,
  ActiveBundleStoresExistsRequest,
  ActiveBundleStoreGroupsResponse,
} from '@/types/menus.types';
import { useSTKContext } from '@/context/context';
import { StoreGroupType } from '@/context/context.types';
import _ from 'lodash';

export const useVariant = (enabled: boolean, stores: string[], storeGroups: StoreGroupType[], variantId: string) => {
  const { apiClient } = useSTKContext();
  const reqBody = { stores, storeGroups: storeGroups.flatMap((st) => st.storeGroupId), variantIds: [variantId] };
  return useQuery<Variant>({
    queryKey: ['variant', { ...stores, variantId }],
    queryFn: async () => {
      const response = await apiClient.put<Variant[]>(`/menu-products/variants`, reqBody);
      return response.data[0];
    },
    enabled,
  });
};

export const useVariants = (
  enabled: boolean,
  stores: string[],
  storeGroups: StoreGroupType[],
  variantIds: string[],
) => {
  const { apiClient } = useSTKContext();
  const reqBody = { stores, storeGroups: storeGroups.flatMap((st) => st.storeGroupId), variantIds };
  return useQuery<Variant[]>({
    queryKey: ['variant', { ...stores, variantIds }],
    queryFn: async () => {
      const response = await apiClient.put<Variant[]>(`/menu-products/variants`, reqBody);
      return response.data;
    },
    enabled,
  });
};

export const usePriceDeviations = (enabled: boolean, stores: string[], variantIds: string[]) => {
  const { apiClient } = useSTKContext();
  const reqBody = { variantIds, stores };
  return useQuery({
    queryKey: ['priceDeviations', { variantIds, stores }],
    queryFn: async () => {
      const response = await apiClient.put(`/menu-products/price-deviations`, reqBody);
      return response.data;
    },
    enabled,
  });
};

export const useStoreMenuDetails = (enabled: boolean, requestBody: string[]) => {
  const reqBody = { stores: requestBody };
  const { apiClient } = useSTKContext();
  return useQuery<AxiosResponse<MenuDetails[]>>({
    queryKey: ['store', ...requestBody],
    queryFn: () => apiClient.put<MenuDetails[]>(`/menu-products`, reqBody),
    enabled,
  });
};
export const useMenuProductsGroup = (
  enabled: boolean,
  stores: string[],
  storeGroups: StoreGroupType[],
  categoryName: string,
) => {
  const { apiClient } = useSTKContext();

  const reqBody = { stores, storeGroups: storeGroups.flatMap((st) => st.storeGroupId), productCategory: categoryName };
  return useQuery<StoreProduct[]>({
    queryKey: ['store-grouped-products', { ...stores, categoryName }],
    queryFn: async () => {
      const response = await apiClient.put<StoreProduct[]>(`/menu-products`, reqBody);
      return response.data;
    },
    enabled,
  });
};
export const useProductCategories = (enabled: boolean, stores: string[], storeGroups: StoreGroupType[]) => {
  const { apiClient } = useSTKContext();

  return useQuery<MenuCategoryType[]>({
    queryKey: ['product-categories'],
    queryFn: async () => {
      const response = await apiClient.get<MenuCategoryType[]>('/menu-products/all-categories');
      return response.data;
    },
    enabled,
  });
};

export const useUpdateProductPrice = () => {
  const { apiClient } = useSTKContext();
  return useMutation(async (requestBody: VariantPriceUpdateRequest[]) => {
    const response = await apiClient.put('/menu-products/update-variant-price', requestBody);
    return response.data;
  });
};

export const useRemoveProduct = () => {
  const { apiClient } = useSTKContext();
  return useMutation(async (requestBody: ProductRemoveRequest) => {
    const response = await apiClient.put('/menu-products/remove', requestBody);
    return response.data;
  });
};

export const useActiveBundleStoreGroups = (enabled: boolean, variantIds: string[], stores: string[]) => {
  const { apiClient } = useSTKContext();

  return useQuery<ActiveBundleStoreGroupsResponse[]>({
    queryKey: ['active-bundles-exists', variantIds, stores],
    queryFn: async () => {
      const requestBody: ActiveBundleStoresExistsRequest = {
        variantIds,
        stores,
      };
      const response = await apiClient.post<ActiveBundleStoreGroupsResponse[]>(
        '/menu-products/active-bundles/exists',
        requestBody,
      );
      return response.data;
    },
    enabled: enabled && variantIds.length > 0 && stores.length > 0,
    staleTime: 0,
    refetchOnMount: 'always',
  });
};
